import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ChevronLeft, Briefcase, MapPin, DollarSign, Award, Users, 
  Calendar, Mail, FileText, Plus, Share2, Search, X, 
  Eye, Edit2, Trash2, ArrowUpDown, Filter, Download, UserPlus, Loader, Send
} from 'lucide-react';
import { IoLogoWhatsapp } from 'react-icons/io5';
import { jobsAPI, candidatesAPI, clientsAPI } from '../../services/api';
import { toast } from 'react-toastify';
import CreateCandidateModal from '../Candidates/CreateCandidateModal';
import EditCandidateModal from '../Candidates/EditCandidateModal';
import CandidatePreview from '../Candidates/CandidatePreview';
import WhatsAppTemplatePanel from '../Templates/WhatsAppTemplatePanel';
import { getWhatsAppNumber, getWhatsAppNumberSource } from '../Templates/templateUtils';

const SHARE_TYPES = {
  LINK: {
    key: 'LINK', label: 'Send Portal Link',
    sublabel: 'Client gets a URL to review candidates in their browser',
    icon: Share2, color: 'indigo',
    apiMethod: (linkId) => candidatesAPI.sendLinkEmail(linkId),
    successMsg: 'Portal link email sent successfully!',
  },
  ATTACHMENT: {
    key: 'ATTACHMENT', label: 'Send Excel Attachment',
    sublabel: 'Client gets an Excel file with all candidate details',
    icon: FileText, color: 'emerald',
    apiMethod: (linkId) => candidatesAPI.sendAttachmentEmail(linkId),
    successMsg: 'Candidate list attachment sent successfully!',
  },
};

const ShareModal = ({ selectedClient, selectedJob, selectedCandidates, emailInput, setEmailInput, expiryHours, setExpiryHours, onClose, onSuccess, applications = [] }) => {
  const [shareType, setShareType] = useState('LINK');
  const [busy, setBusy] = useState(false);

  const handleConfirm = async () => {
    if (!emailInput || !emailInput.includes('@')) { toast.error('Please enter a valid email address'); return; }
    if (!shareType) { toast.error('Please select a delivery method'); return; }
    setBusy(true);
    try {
      const mappedApplicationIds = selectedCandidates.map(id => {
        const app = applications.find(a => a.id === id);
        return app?.applicationId || id;
      });

      const payload = { 
        clientId: selectedClient.id, 
        jobId: selectedJob.id, 
        clientEmail: emailInput, 
        applicationIds: mappedApplicationIds, 
        expiryHours: parseInt(expiryHours, 10) 
      };
      const createRes = await candidatesAPI.generateShareableLink(payload);
      const linkId = createRes.data?.linkId || createRes.data?.id;
      if (!linkId) { toast.error('Link created but ID not returned'); setBusy(false); return; }
      await SHARE_TYPES[shareType].apiMethod(linkId);
      toast.success(SHARE_TYPES[shareType].successMsg);
      if (createRes.data?.shareableUrl) { navigator.clipboard.writeText(createRes.data.shareableUrl).catch(() => {}); toast.info('Portal link also copied to clipboard'); }
      onSuccess(); onClose();
    } catch (err) { toast.error('Failed: ' + (err.response?.data?.message || err.message)); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden animate-slideInRight flex flex-col max-h-[90vh]">
        <div className="px-6 pt-6 pb-4 border-b border-gray-100 flex items-center justify-between flex-shrink-0">
          <div>
            <p className="text-xs font-semibold text-indigo-600 uppercase tracking-wider mb-0.5">Share Candidates</p>
            <h2 className="text-lg font-bold text-gray-900">Confirm Details & Select Method</h2>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 transition-colors"><X size={18} /></button>
        </div>
        
        <div className="flex-1 overflow-y-auto">
          <div className="px-6 py-5 space-y-6">
            {/* Summary */}
            <div className="bg-gray-50 rounded-xl p-4 text-xs text-gray-600 border border-gray-200">
              <div className="flex justify-between mb-1.5"><span className="text-gray-400 uppercase tracking-wider font-semibold">Company</span><span className="font-bold text-gray-800">{selectedClient?.companyName}</span></div>
              <div className="flex justify-between mb-1.5"><span className="text-gray-400 uppercase tracking-wider font-semibold">Job</span><span className="font-bold text-gray-800">{selectedJob?.jobCode} — {selectedJob?.jobTitle}</span></div>
              <div className="flex justify-between pt-1.5 border-t border-gray-200"><span className="text-gray-400 uppercase tracking-wider font-semibold">Selected</span><span className="font-bold text-indigo-700 text-sm">{selectedCandidates.length} Candidates</span></div>
            </div>

            {/* Recipient */}
            <div className="space-y-4">
              <h3 className="text-sm font-bold text-gray-900 flex items-center gap-2">
                <div className="w-1 h-4 bg-indigo-500 rounded-full"></div>
                Recipient Information
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-semibold text-gray-600 uppercase tracking-wider mb-1.5 block">Recipient Email <span className="text-red-500">*</span></label>
                  <div className="relative">
                    <Mail size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input type="email" value={emailInput} onChange={(e) => setEmailInput(e.target.value)} placeholder="client@example.com" className="w-full pl-9 pr-4 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-400 bg-white" />
                  </div>
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-600 uppercase tracking-wider mb-1.5 block">Link Expiry (hours)</label>
                  <input type="number" value={expiryHours} onChange={(e) => setExpiryHours(e.target.value)} min="1" max="168" className="w-full px-4 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-400 bg-white" />
                </div>
              </div>
            </div>

            {/* Method */}
            <div className="space-y-4">
              <h3 className="text-sm font-bold text-gray-900 flex items-center gap-2">
                <div className="w-1 h-4 bg-indigo-500 rounded-full"></div>
                Delivery Method
              </h3>
              <div className="grid grid-cols-1 gap-3">
                {Object.values(SHARE_TYPES).map((type) => {
                  const Icon = type.icon;
                  const isActive = shareType === type.key;
                  return (
                    <button key={type.key} onClick={() => setShareType(type.key)}
                      className={`w-full flex items-center gap-4 p-4 rounded-xl border-2 transition-all text-left ${isActive ? (type.color === 'indigo' ? 'border-indigo-600 bg-indigo-50' : 'border-emerald-600 bg-emerald-50') : 'border-gray-100 hover:border-gray-200 hover:bg-gray-50'}`}>
                      <div className={`w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 ${isActive ? (type.color === 'indigo' ? 'bg-indigo-600 text-white' : 'bg-emerald-600 text-white') : (type.color === 'indigo' ? 'bg-indigo-100 text-indigo-600' : 'bg-emerald-100 text-emerald-600')}`}><Icon size={22} /></div>
                      <div className="flex-1">
                        <div className="font-semibold text-gray-900 text-sm">{type.label}</div>
                        <div className="text-xs text-gray-500 mt-0.5">{type.sublabel}</div>
                      </div>
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${isActive ? (type.color === 'indigo' ? 'border-indigo-600' : 'border-emerald-600') : 'border-gray-200'}`}>
                        {isActive && <div className={`w-2.5 h-2.5 rounded-full ${type.color === 'indigo' ? 'bg-indigo-600' : 'bg-emerald-600'}`} />}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        <div className="px-6 py-6 border-t border-gray-100 bg-gray-50 flex-shrink-0">
          <button onClick={handleConfirm} disabled={busy || !emailInput || !shareType} className="w-full px-4 py-3 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-base font-bold flex items-center justify-center gap-3 disabled:opacity-60 transition-all shadow-lg active:scale-[0.98]">
            {busy ? <><Loader size={20} className="animate-spin" /> Sending…</> : <><Send size={20} /> Confirm & Send sharing</>}
          </button>
        </div>
      </div>
    </div>
  );
};

const JobDetailsPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [job, setJob] = useState(null);
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [appsLoading, setAppsLoading] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedAppForPreview, setSelectedAppForPreview] = useState(null);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCandidates, setSelectedCandidates] = useState([]);
  const [showShareModal, setShowShareModal] = useState(false);
  const [clientData, setClientData] = useState(null);
  const [emailInput, setEmailInput] = useState('');
  const [expiryHours, setExpiryHours] = useState(48);
  const [whatsappPanelCandidate, setWhatsappPanelCandidate] = useState(null);
  const [showEditCandidateModal, setShowEditCandidateModal] = useState(false);
  const [candidateToEdit, setCandidateToEdit] = useState(null);

  const BRAND = "#1C4E6D";
  const BRAND_DARK = "#143a52";

  useEffect(() => {
    fetchJobDetails();
    fetchApplications();
  }, [id]);

  const fetchJobDetails = async () => {
    try {
      const response = await jobsAPI.getJobById(id);
      setJob(response.data);
      if (response.data?.clientId) {
        fetchClientData(response.data.clientId);
      } else if (response.data?.client) {
        // Fallback: Try to find client by name if ID isn't directly available
        searchClientByName(response.data.client);
      }
    } catch (error) {
      toast.error('Failed to fetch job details');
      navigate('/ats/jobs');
    }
  };

  const fetchClientData = async (clientId) => {
    try {
      const res = await clientsAPI.getClientById(clientId);
      setClientData(res.data);
      setEmailInput(res.data?.pocEmail || '');
    } catch (err) {
      console.error("Failed to fetch client data:", err);
    }
  };

  const searchClientByName = async (name) => {
    try {
      const res = await clientsAPI.getAllClients();
      const clients = Array.isArray(res.data) ? res.data : (res.data.content || []);
      const target = clients.find(c => c.companyName === name);
      if (target) {
        setClientData(target);
        setEmailInput(target.pocEmail || '');
      }
    } catch (err) {
      console.error("Failed to search client:", err);
    }
  };

  const handleDownload = async (url, filename) => {
    if (!url) return;
    try {
      const response = await fetch(url);
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = filename || 'resume.pdf';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error('Download failed:', error);
      // Fallback: if fetch fails (e.g. CORS), try opening in new tab
      window.open(url, '_blank');
    }
  };

  const fetchApplications = async () => {
    setAppsLoading(true);
    try {
      const response = await candidatesAPI.getApplicationsByJobId(id);
      setApplications(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Failed to fetch applications:', error);
    } finally {
      setAppsLoading(false);
      setLoading(false);
    }
  };

  const handleAddCandidate = () => {
    setShowCreateModal(true);
  };

  const handleShareToClient = () => {
    if (selectedCandidates.length === 0) {
      toast.warning('Please select at least one candidate to share');
      return;
    }
    setShowShareModal(true);
  };

  const toggleCandidateSelection = (candidateId) => {
    setSelectedCandidates(prev => 
      prev.includes(candidateId) 
        ? prev.filter(id => id !== candidateId) 
        : [...prev, candidateId]
    );
  };

  const toggleAllSelection = () => {
    if (selectedCandidates.length === filteredApps.length) {
      setSelectedCandidates([]);
    } else {
      setSelectedCandidates(filteredApps.map(app => app.id));
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getStatusColor = (status) => {
    const colors = {
      'OPEN': 'bg-green-100 text-green-800 border-green-200',
      'CLOSED': 'bg-red-100 text-red-800 border-red-200',
      'ON_HOLD': 'bg-yellow-100 text-yellow-800 border-yellow-200',
      'DRAFT': 'bg-gray-100 text-gray-800 border-gray-200'
    };
    return colors[status?.toUpperCase()] || 'bg-gray-100 text-gray-800 border-gray-200';
  };

  const getAppStatusColor = (status) => {
    const colors = {
      'APPLIED': 'bg-blue-100 text-blue-800',
      'SCREENING': 'bg-purple-100 text-purple-800',
      'INTERVIEW_SCHEDULED': 'bg-indigo-100 text-indigo-800',
      'SELECTED': 'bg-green-100 text-green-800',
      'REJECTED': 'bg-red-100 text-red-800',
      'OFFERED': 'bg-teal-100 text-teal-800',
      'JOINED': 'bg-emerald-100 text-emerald-800'
    };
    return colors[status?.toUpperCase()] || 'bg-gray-100 text-gray-800';
  };

  const filteredApps = applications.filter(app => {
    const searchLower = searchQuery.toLowerCase();
    return (
      app.firstName?.toLowerCase().includes(searchLower) ||
      app.lastName?.toLowerCase().includes(searchLower) ||
      app.primaryEmail?.toLowerCase().includes(searchLower) ||
      app.applicationId?.toLowerCase().includes(searchLower)
    );
  });

  const handleDeleteCandidate = async (appId) => {
    if (window.confirm('Are you sure you want to remove this candidate application?')) {
      try {
        await candidatesAPI.deleteApplication(appId);
        toast.success('Application removed successfully');
        fetchApplications();
      } catch (error) {
        toast.error('Failed to remove application');
      }
    }
  };

  if (loading || !job) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-50">
        <div className="flex flex-col items-center gap-3">
          <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
          <p className="text-sm text-gray-500">Loading job details…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f8fafc] flex flex-col font-sans">
      {/* ── Top Navigation ── */}
      <div className="bg-white border-b border-gray-200 px-6 py-3 flex items-center justify-between sticky top-0 z-30 shadow-sm flex-shrink-0">
        <div className="flex items-center gap-3">
          <button 
            onClick={() => navigate('/ats/jobs')}
            className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-800 transition-colors"
          >
            <ChevronLeft size={16} /> Jobs
          </button>
          <span className="text-gray-300">/</span>
          <div className="flex items-center gap-2">
            <span className="text-sm font-semibold text-gray-800">{job?.jobTitle}</span>
            <span className="text-xs text-gray-400">({job?.jobCode})</span>
          </div>
        </div>
        <div className="flex items-center gap-3">
           <button 
            onClick={handleAddCandidate}
            className="px-4 py-2 text-sm text-white rounded-lg flex items-center gap-2 shadow-sm transition-all hover:scale-[1.02] active:scale-[0.98]"
            style={{ backgroundColor: BRAND }}
          >
            <UserPlus size={16} />
            Add Candidate
          </button>
        </div>
      </div>

      {/* ── Unified Unified Content Area ── */}
      <div className="flex-1 max-w-[1400px] mx-auto w-full p-6 space-y-6">
        
        {/* ── Job Details Section ── */}
        <section className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="p-8 grid grid-cols-1 lg:grid-cols-12 gap-8">
            
            {/* Left: Basic Info */}
            <div className="lg:col-span-5 space-y-8">
              <div className="flex items-center gap-5">
                <div 
                  className="w-20 h-20 rounded-2xl flex items-center justify-center text-white text-3xl font-bold shadow-lg"
                  style={{ backgroundColor: BRAND }}
                >
                  {job.jobTitle?.charAt(0).toUpperCase()}
                </div>
                <div>
                  <h1 className="font-bold text-gray-900 text-2xl leading-tight mb-1">{job.jobTitle}</h1>
                  <p className="text-base text-gray-500">{job.client || 'Internal Posting'}</p>
                </div>
              </div>

              <div className="flex flex-wrap gap-3">
                <span className={`px-4 py-1.5 text-xs font-bold rounded-full border uppercase tracking-wider ${getStatusColor(job.jobStatus)}`}>
                  {job.jobStatus || 'OPEN'}
                </span>
                <span className="px-4 py-1.5 text-xs font-bold rounded-full bg-blue-50 text-blue-700 border border-blue-100 uppercase tracking-wider">
                  {job.jobPriority || 'MEDIUM'} PRIORITY
                </span>
              </div>

              <div className="grid grid-cols-2 gap-y-6 gap-x-8 pt-2">
                <DetailRow 
                  icon={<MapPin size={16} />} 
                  label="Location" 
                  value={job.workLocation && job.workLocation !== 'N/A' ? `${job.workLocation} ${job.workModel ? `(${job.workModel})` : ''}` : (job.workModel || 'N/A')} 
                />
                <DetailRow icon={<Briefcase size={16} />} label="Type" value={job.jobType?.replace(/_/g, ' ') || 'N/A'} />
                <DetailRow 
                  icon={<DollarSign size={16} />} 
                  label="Compensation" 
                  value={(job.compensationMin !== null && job.compensationMin !== undefined) ? `${job.compensationCurrency || 'INR'} ${Number(job.compensationMin).toLocaleString()} - ${Number(job.compensationMax).toLocaleString()}` : 'N/A'} 
                />
                <DetailRow 
                  icon={<Award size={16} />} 
                  label="Experience" 
                  value={(job.experienceMin !== null && job.experienceMin !== undefined) ? `${job.experienceMin} - ${job.experienceMax} years` : 'N/A'} 
                />
                <DetailRow icon={<Users size={16} />} label="Openings" value={job.numberOfOpenings || 'N/A'} />
                <DetailRow icon={<Calendar size={16} />} label="Created" value={formatDate(job.createdAt)} />
              </div>

              <div>
                <h3 className="text-[11px] font-bold text-gray-400 uppercase tracking-widest mb-3">Primary Skills</h3>
                <div className="flex flex-wrap gap-2">
                  {job.primarySkills && job.primarySkills.length > 0 ? (
                    job.primarySkills.map((skill, i) => (
                      <span key={i} className="px-3 py-1.5 bg-gray-50 text-gray-600 text-xs font-semibold rounded-lg border border-gray-200">
                        {skill}
                      </span>
                    ))
                  ) : (
                    <span className="text-xs text-gray-400 italic">No skills listed</span>
                  )}
                </div>
              </div>
            </div>

            {/* Right: Description */}
            <div className="lg:col-span-7 bg-gray-50/50 rounded-2xl p-8 border border-gray-100 h-fit">
              <div className="flex items-center gap-2 mb-6">
                <FileText size={20} style={{ color: BRAND }} />
                <h3 className="text-lg font-bold text-gray-900">Job Description</h3>
              </div>
              <div className="prose prose-sm max-w-none text-gray-600 leading-relaxed min-h-[150px]">
                {job.jobDescription ? (
                  <div className="whitespace-pre-wrap">{job.jobDescription}</div>
                ) : (
                  <div className="flex flex-col items-center justify-center py-12 text-gray-400">
                    <FileText size={32} className="opacity-20 mb-2" />
                    <p className="text-sm">No job description provided.</p>
                  </div>
                )}
              </div>
              
              {job.keyResponsibilities && job.keyResponsibilities.length > 0 && (
                <div className="mt-8 pt-8 border-t border-gray-200">
                  <h3 className="text-sm font-bold text-gray-800 mb-4 uppercase tracking-wide">Key Responsibilities</h3>
                  <ul className="list-disc pl-5 space-y-2 text-sm text-gray-600">
                    {job.keyResponsibilities.map((resp, i) => (
                      <li key={i}>{resp}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        </section>

        {/* ── Candidate List Section ── */}
        <section className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden mb-12">
          <div className="px-8 py-6 border-b border-gray-100 flex items-center justify-between bg-white">
            <div className="flex items-center gap-3">
              <h2 className="text-xl font-bold text-gray-800">Candidates</h2>
              <span className="px-2.5 py-0.5 bg-gray-100 text-gray-500 text-xs font-bold rounded-full">
                {filteredApps.length} Total
              </span>
            </div>
            
            <div className="flex items-center gap-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
                <input 
                  type="text" 
                  placeholder="Search candidates..." 
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 pr-4 py-2.5 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 w-80 transition-all shadow-sm"
                />
              </div>
              
              <div className="h-10 w-[1px] bg-gray-200 mx-2" />

              <div className="flex items-center gap-4">
                <div className="text-xs text-gray-500 font-medium">
                  <span className="text-blue-600 font-bold">{selectedCandidates.length}</span> candidates selected
                </div>
                <button 
                  onClick={handleShareToClient}
                  disabled={selectedCandidates.length === 0}
                  className="px-5 py-2.5 bg-[#1C4E6D] hover:bg-[#143a52] text-white rounded-xl flex items-center gap-2 text-xs font-bold shadow-md transition-all hover:scale-[1.02] active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed disabled:scale-100"
                >
                  <Share2 size={16} />
                  Share to Client
                </button>
              </div>
            </div>
          </div>

          <div className="overflow-x-auto">
            {appsLoading ? (
              <div className="flex items-center justify-center h-64">
                <div className="w-8 h-8 border-3 border-blue-600 border-t-transparent rounded-full animate-spin" />
              </div>
            ) : filteredApps.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-20 text-center">
                <div className="w-20 h-20 bg-gray-50 rounded-3xl flex items-center justify-center mb-6 border border-gray-100">
                  <Users size={40} className="text-gray-300" />
                </div>
                <h3 className="text-gray-900 text-lg font-bold mb-2">No Candidates Found</h3>
                <p className="text-gray-500 text-sm max-w-sm mx-auto mb-8">
                  There are currently no candidates associated with this job. Add your first candidate to get started.
                </p>
                <button 
                  onClick={handleAddCandidate}
                  className="inline-flex items-center gap-2 px-6 py-3 bg-white border border-gray-200 rounded-xl text-sm font-bold text-gray-700 hover:bg-gray-50 hover:border-gray-300 shadow-sm transition-all"
                >
                  <Plus size={18} /> Add Candidate
                </button>
              </div>
            ) : (
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="bg-gray-50/50 border-b border-gray-100">
                    <th className="px-8 py-5 w-10">
                      <input 
                        type="checkbox" 
                        checked={filteredApps.length > 0 && selectedCandidates.length === filteredApps.length}
                        onChange={toggleAllSelection}
                        className="w-5 h-5 rounded-md border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                      />
                    </th>
                    <th className="px-6 py-5 text-xs font-bold text-gray-400 uppercase tracking-widest">Name</th>
                    <th className="px-6 py-5 text-xs font-bold text-gray-400 uppercase tracking-widest">Phone</th>
                    <th className="px-6 py-5 text-xs font-bold text-gray-400 uppercase tracking-widest">Email</th>
                    <th className="px-6 py-5 text-xs font-bold text-gray-400 uppercase tracking-widest text-center">Resume</th>
                    <th className="px-6 py-5 text-xs font-bold text-gray-400 uppercase tracking-widest">Status</th>
                    <th className="px-8 py-5 text-xs font-bold text-gray-400 uppercase tracking-widest text-center">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {filteredApps.map((app) => (
                    <tr key={app.id} className={`hover:bg-blue-50/20 transition-all group ${selectedCandidates.includes(app.id) ? 'bg-blue-50/40' : ''}`}>
                      <td className="px-8 py-5">
                        <input 
                          type="checkbox" 
                          checked={selectedCandidates.includes(app.id)}
                          onChange={() => toggleCandidateSelection(app.id)}
                          className="w-5 h-5 rounded-md border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                        />
                      </td>
                      <td className="px-6 py-5">
                        <div className="flex items-center gap-4">
                          <div className="w-10 h-10 rounded-xl bg-gray-100 flex items-center justify-center text-sm font-bold text-gray-600 border border-gray-200 shadow-sm">
                            {app.firstName?.[0]}{app.lastName?.[0]}
                          </div>
                          <button 
                            onClick={() => navigate(`/candidates/${app.id}`)}
                            className="text-sm font-bold text-gray-900 hover:text-[#1C4E6D] hover:underline transition-all text-left"
                          >
                            {app.firstName} {app.lastName}
                          </button>
                        </div>
                      </td>
                      <td className="px-6 py-5">
                        {(() => {
                          const phone = app.primaryPhone || app.mobileNumber || app.phone;
                          if (!phone) return <span className="text-sm text-gray-400">—</span>;
                          const validWA = getWhatsAppNumber({ phone });
                          return (
                            <div className="flex items-center gap-1.5">
                              <a href={`tel:${phone}`} className="text-sm text-gray-700 font-medium hover:text-blue-600 hover:underline transition-colors">{phone}</a>
                              <button
                                onClick={e => { e.stopPropagation(); if (validWA) setWhatsappPanelCandidate(app); }}
                                title={validWA ? 'Send WhatsApp' : 'Not a valid WhatsApp number'}
                                disabled={!validWA}
                                className={`p-0.5 rounded transition-colors ${validWA ? 'text-[#25D366] hover:bg-green-50 cursor-pointer' : 'text-gray-300 cursor-not-allowed'}`}
                              >
                                <IoLogoWhatsapp size={15} />
                              </button>
                            </div>
                          );
                        })()}
                      </td>
                      <td className="px-6 py-5">
                        <p className="text-sm text-gray-600 font-medium">{app.primaryEmail || '—'}</p>
                      </td>
                      <td className="px-6 py-5">
                        <div className="flex items-center justify-center gap-3">
                          {app.resumeUrl ? (
                            <>
                              <button 
                                onClick={() => window.open(app.resumeUrl, '_blank')}
                                title="View Resume"
                                className="p-1.5 text-blue-500 hover:bg-blue-50 rounded-lg transition-colors"
                              >
                                <Eye size={16} />
                              </button>
                              <button 
                                onClick={() => handleDownload(app.resumeUrl, `${app.firstName}_${app.lastName}_Resume.pdf`)}
                                title="Download Resume"
                                className="p-1.5 text-emerald-500 hover:bg-emerald-50 rounded-lg transition-colors"
                              >
                                <Download size={16} />
                              </button>
                            </>
                          ) : (
                            <span className="text-xs text-gray-400 italic">No resume</span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-5">
                        <span className={`px-3 py-1 text-[10px] font-bold rounded-lg border uppercase tracking-tight ${getAppStatusColor(app.status)}`}>
                          {app.status === 'Selected' ? 'Selected / Hired' : (app.status?.replace(/_/g, ' ') || 'APPLIED')}
                        </span>
                      </td>
                      <td className="px-8 py-5">
                        <div className="flex items-center justify-center gap-1.5">
                          <button 
                            onClick={() => { setSelectedAppForPreview(app); setShowPreviewModal(true); }}
                            title="Preview Profile"
                            className="p-2 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-all"
                          >
                            <Eye size={16} />
                          </button>
                          <button 
                            onClick={() => { setCandidateToEdit(app); setShowEditCandidateModal(true); }}
                            title="Edit Application"
                            className="p-2 text-slate-400 hover:text-blue-800 hover:bg-blue-50 rounded-lg transition-all"
                          >
                            <Edit2 size={16} />
                          </button>
                          <button 
                            onClick={() => handleDeleteCandidate(app.id)}
                            title="Delete Application"
                            className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </section>
      </div>

      {/* ── Modals ── */}
      {showCreateModal && (
        <CreateCandidateModal
          onClose={() => setShowCreateModal(false)}
          onSave={() => {
            setShowCreateModal(false);
            fetchApplications();
            toast.success('Candidate added successfully');
          }}
        />
      )}

      {showEditCandidateModal && candidateToEdit && (
        <EditCandidateModal 
          candidate={candidateToEdit}
          onClose={() => {
            setShowEditCandidateModal(false);
            setCandidateToEdit(null);
          }}
          onUpdate={() => {
            setShowEditCandidateModal(false);
            setCandidateToEdit(null);
            fetchApplications();
            toast.success('Candidate updated successfully');
          }}
        />
      )}

      {showPreviewModal && selectedAppForPreview && (
        <CandidatePreview 
          candidate={selectedAppForPreview}
          onClose={() => {
            setShowPreviewModal(false);
            setSelectedAppForPreview(null);
            fetchApplications(); // Refresh if status might have changed
          }}
        />
      )}

      {showShareModal && job && clientData && (
        <ShareModal 
          selectedClient={clientData}
          selectedJob={job}
          selectedCandidates={selectedCandidates}
          applications={applications}
          emailInput={emailInput}
          setEmailInput={setEmailInput}
          expiryHours={expiryHours}
          setExpiryHours={setExpiryHours}
          onClose={() => setShowShareModal(false)}
          onSuccess={() => {
            setSelectedCandidates([]);
            setShowShareModal(false);
          }}
        />
      )}

      <WhatsAppTemplatePanel
        isOpen={Boolean(whatsappPanelCandidate)}
        onClose={() => setWhatsappPanelCandidate(null)}
        module="ATS"
        context={whatsappPanelCandidate ? {
          candidateName: `${whatsappPanelCandidate.firstName || ''} ${whatsappPanelCandidate.lastName || ''}`.trim(),
          jobRole: whatsappPanelCandidate.currentJobTitle || '',
          senderName: localStorage.getItem('email') || '',
          agencyName: localStorage.getItem('organizationName') || '',
        } : {}}
        recipient={whatsappPanelCandidate ? {
          phone: getWhatsAppNumber({ phone: whatsappPanelCandidate.primaryPhone || whatsappPanelCandidate.mobileNumber || whatsappPanelCandidate.phone }),
          phoneSource: getWhatsAppNumberSource({ phone: whatsappPanelCandidate.primaryPhone || whatsappPanelCandidate.mobileNumber || whatsappPanelCandidate.phone }),
          name: `${whatsappPanelCandidate.firstName || ''} ${whatsappPanelCandidate.lastName || ''}`.trim(),
        } : null}
      />

      <style dangerouslySetInnerHTML={{ __html: `
        @keyframes slideInRight {
          from { transform: translateX(100%); }
          to { transform: translateX(0); }
        }
        .animate-slideInRight {
          animation: slideInRight 0.3s ease-out;
        }
        .custom-scrollbar::-webkit-scrollbar {
          width: 4px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: #f1f1f1;
          border-radius: 10px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: #e2e8f0;
          border-radius: 10px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: #cbd5e1;
        }
      `}} />
    </div>
  );
};

const DetailRow = ({ icon, label, value }) => (
  <div className="flex items-start gap-3">
    <div className="mt-0.5 text-gray-400">{icon}</div>
    <div className="min-w-0">
      <p className="text-[10px] text-gray-400 font-bold uppercase tracking-wider leading-none mb-1">{label}</p>
      <p className="text-xs text-gray-800 font-medium truncate">{value || 'N/A'}</p>
    </div>
  </div>
);

export default JobDetailsPage;
